import React, { useState, useEffect } from 'react'
import { Modal } from '@/shared/components/ui'
import EntryForm, { EntryFormInput } from './EntryForm'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useToast } from '@/shared/hooks/useToast'
import { useBilling } from '@/modules/billing/BillingContext'
import {
  createEntry,
  updateEntry,
  fetchEntry,
  markAsPaid,
} from '@/modules/billing/entries/entriesService'
import type {
  AnyEntry,
  FinancialEntry,
  VirtualEntry,
  PaymentMethod,
} from '@/modules/billing/entries/entriesTypes'
import { listParties, updateParty, upsertParty } from '@/modules/registrations/contacts/contactsService'
import type { Party } from '@/modules/registrations/contacts/contactsTypes'

interface Props {
  isOpen: boolean
  entryType: 'receivable' | 'payable'
  initialEntry?: AnyEntry | null
  defaultDueDate?: string
  onClose: () => void
  onSaved?: (entry: FinancialEntry) => void
}

export default function EntryModal({ isOpen, entryType, initialEntry = null, defaultDueDate, onClose, onSaved }: Props) {
  const { currentUnit, userRole } = useUnit()
  const { t, locale } = useTranslation()
  const toast = useToast()
  const { invalidateEntries } = useBilling()

  const [parties, setParties] = useState<Party[]>([])

  const isVirtualEntry = (entry: AnyEntry | null | undefined): entry is VirtualEntry => {
    return !!entry && (entry as VirtualEntry).isVirtual === true
  }

  useEffect(() => {
    let mounted = true
    if (!currentUnit?.id) {
      setParties([])
      return
    }
    listParties(currentUnit.id)
      .then(data => { if (mounted) setParties(data) })
      .catch(err => console.error('[EntryModal] listParties error:', err))
    return () => { mounted = false }
  }, [currentUnit?.id])

  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 640px)')
    const update = () => setIsMobile(!!mq.matches)
    update()
    try { mq.addEventListener('change', update) } catch (e) { mq.addListener(update) }
    return () => { try { mq.removeEventListener('change', update) } catch (e) { mq.removeListener(update) } }
  }, [])

  const emptyForm = (defaultDueDate?: string): EntryFormInput => ({
    description: '',
    amount: '',
    due_date: defaultDueDate ?? new Date().toISOString().split('T')[0],
    party_name_snapshot: '',
    party_id: undefined,
    party_whatsapp_snapshot: '',
    party_pix_key_snapshot: '',
    payment_method_snapshot: '',
    notes: '',
    paid_on_launch: false,
  })

  const [formData, setFormData] = useState<EntryFormInput>(emptyForm())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!initialEntry) {
      setFormData(emptyForm(defaultDueDate))
      setErrors({})
      return
    }

    setFormData({
      description: initialEntry.description || '',
      amount: String(initialEntry.amount || ''),
      due_date: initialEntry.due_date || new Date().toISOString().split('T')[0],
      party_name_snapshot: initialEntry.party_name_snapshot || '',
      party_id: initialEntry.party_id ?? undefined,
      party_whatsapp_snapshot: initialEntry.party_whatsapp_snapshot || '',
      party_pix_key_snapshot: initialEntry.party_pix_key_snapshot || '',
      payment_method_snapshot: initialEntry.payment_method_snapshot || '',
      notes: initialEntry.notes || '',
      paid_on_launch: false,
    })
    setErrors({})
  }, [initialEntry, defaultDueDate])

  const validate = (data: EntryFormInput) => {
    const errs: Record<string, string> = {}
    if (!data.description.trim()) errs.description = t('billing.messages.descriptionRequired') || 'DescriÃ§Ã£o Ã© obrigatÃ³ria'
    if (!data.amount || Number(data.amount) <= 0) errs.amount = t('billing.messages.amountPositive') || 'Valor deve ser positivo'
    if (!data.due_date) errs.due_date = t('billing.messages.dueDateRequired') || 'Data de vencimento Ã© obrigatÃ³ria'
    return errs
  }

  const notifySaved = async (entry: FinancialEntry, message: string) => {
    toast.success(message)

    // Quando EntryActions controla o fluxo, ele precisa receber onSaved antes de
    // invalidar a lista. Invalidar aqui desmonta o EntryActions e pode impedir
    // a pergunta "sÃ³ esta / esta e futuras" de aparecer.
    if (onSaved) {
      onSaved(entry)
    } else {
      await invalidateEntries()
    }

    onClose()
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!currentUnit?.id) return toast.error('Unidade nÃ£o selecionada')

    const errs = validate(formData)
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    try {
      // Resolve or create party
      const partyName = formData.party_name_snapshot.trim()
      let partyId = formData.party_id ?? undefined

      if (partyId) {
        if (partyName) {
          try {
            await updateParty(partyId, {
              name: partyName,
              whatsapp: formData.party_whatsapp_snapshot.trim() || undefined,
            })
          } catch (err) {
            console.warn('[EntryModal] updateParty warning:', err)
          }
        }
      } else if (partyName) {
        partyId = await upsertParty(currentUnit.id, partyName, formData.party_whatsapp_snapshot.trim() || undefined)
      }

      const basePayload = {
        description: formData.description.trim(),
        amount: Number(formData.amount),
        due_date: formData.due_date,
        party_id: partyId,
        party_name_snapshot: partyName || undefined,
        party_whatsapp_snapshot: formData.party_whatsapp_snapshot.trim() || undefined,
        party_pix_key_snapshot: formData.party_pix_key_snapshot.trim() || undefined,
        payment_method_snapshot: (formData.payment_method_snapshot as PaymentMethod) || undefined,
        notes: formData.notes.trim() || undefined,
      }

      if (initialEntry) {
        // Entrada virtual nÃ£o existe no banco. Ao editar, ela precisa virar uma
        // conta fÃ­sica vinculada ao mesmo recurrence_template_id. Depois o
        // EntryActions recebe essa conta real em onSaved e abre a mesma pergunta
        // usada para contas fÃ­sicas recorrentes.
        if (isVirtualEntry(initialEntry)) {
          const recurrenceTemplateId = initialEntry.recurrence_template_id
          if (!recurrenceTemplateId) throw new Error('Template da recorrÃªncia nÃ£o encontrado')

          let created = await createEntry({
            unit_id: currentUnit.id,
            entry_type: initialEntry.entry_type ?? entryType,
            ...basePayload,
            // Preserve occurrence date so SQL dedup works even if due_date is edited
            original_due_date: initialEntry.due_date,
            recurrence_template_id: recurrenceTemplateId,
            notify_on_due: (initialEntry as any).notify_on_due ?? false,
            notify_time: (initialEntry as any).notify_time ?? undefined,
            notify_offset_minutes: (initialEntry as any).notify_offset_minutes ?? undefined,
          }, userRole || undefined)

          if (formData.paid_on_launch && created.status !== 'paid') {
            const todayIso = new Date().toISOString().split('T')[0]
            const paidDate = formData.due_date <= todayIso ? formData.due_date : todayIso
            await markAsPaid(created.id, paidDate, userRole || undefined)
            const refreshed = await fetchEntry(created.id)
            if (refreshed) created = refreshed
          }

          await notifySaved(created, 'LanÃ§amento atualizado')
          return
        }

        let updated = await updateEntry(initialEntry.id, basePayload, userRole || undefined)

        if (formData.paid_on_launch && updated.status !== 'paid') {
          const todayIso = new Date().toISOString().split('T')[0]
          const paidDate = formData.due_date <= todayIso ? formData.due_date : todayIso
          await markAsPaid(updated.id, paidDate, userRole || undefined)
          const refreshed = await fetchEntry(updated.id)
          if (refreshed) updated = refreshed
        }

        await notifySaved(updated, 'LanÃ§amento atualizado')
        return
      }

      let entry = await createEntry({
        unit_id: currentUnit.id,
        entry_type: entryType,
        ...basePayload,
      }, userRole || undefined)

      if (formData.paid_on_launch) {
        const todayIso = new Date().toISOString().split('T')[0]
        const paidDate = formData.due_date <= todayIso ? formData.due_date : todayIso
        await markAsPaid(entry.id, paidDate, userRole || undefined)
        const refreshed = await fetchEntry(entry.id)
        if (refreshed) entry = refreshed
      }

      await notifySaved(entry, 'LanÃ§amento criado')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={initialEntry ? (t('billing.entries.record.edit') || 'Editar') : (entryType === 'receivable' ? (t('billing.entries.record.new.receivable') || 'Nova receita') : (t('billing.entries.record.new.payable') || 'Nova despesa'))}
      size="lg"
      fullScreen={false}
      mobileTopAligned={true}
      headerLeft={isMobile ? (
        <button
          onClick={() => !submitting && onClose()}
          className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors"
          title={t('actions.cancel') || 'Cancelar'}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : undefined}
      headerRight={(
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const form = document.getElementById('entity-edit-form') as HTMLFormElement | null
              if (form && typeof form.requestSubmit === 'function') form.requestSubmit()
              else if (form) form.submit()
            }}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 transition-colors"
            title={initialEntry ? (t('actions.update') || 'Atualizar') : (t('actions.create') || 'Criar')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={() => !submitting && onClose()}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              title={t('actions.cancel') || 'Cancelar'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}
    >
      <form id="entity-edit-form" onSubmit={handleSubmit}>
        <EntryForm
          formData={formData}
          setFormData={setFormData}
          formErrors={errors}
          submitting={submitting}
          editingEntity={initialEntry ?? null}
          mode={initialEntry ? 'edit' : 'create'}
          parties={parties}
          paymentMethodOptions={(() => {
            const opts: { value: string; label: string }[] = []
            opts.push({ value: 'cash', label: t('billing.entries.paymentMethods.cash') })
            if (locale === 'pt-BR') opts.push({ value: 'pix', label: t('billing.entries.paymentMethods.pix') })
            opts.push({ value: 'direct_debit', label: t('billing.entries.paymentMethods.direct_debit') })
            if (locale === 'pt-BR') opts.push({ value: 'dda', label: t('billing.entries.paymentMethods.dda') })
            opts.push({ value: 'card', label: t('billing.entries.paymentMethods.card') })
            opts.push({ value: 'deposit', label: t('billing.entries.paymentMethods.deposit') })
            if (locale === 'pt-BR') opts.push({ value: 'boleto', label: t('billing.entries.paymentMethods.boleto') })
            return opts
          })()}
          setConfirmSubmitOpen={() => {}}
        />
      </form>
    </Modal>
  )
}
